import {
  isDealStatusAllNonTransitEditable,
  isRepriceWindow,
} from '../deal-series/deal-status';
import {
  MaturityOrder,
  MaturityStatusColor,
  OrderStatusEnum,
  OrderStatusIcon,
  successOrderStatuses,
} from '../deal-series/deal-series.types';
import {
  hasOrderAmountApplied,
  isOrderPassesSubmissionAmountRules,
  mapMaturityOrderForSubmission,
  getBaseQuantityCellValidationState,
  isSplitMaturityParentRow,
  isOrderInTransit,
  isSubmittedQuantityBelowPar,
  orderAmountChangeFlagActive,
  orderHasEditsFromAcceptedBaseline,
} from '../deal-series/deal-series.util';
import {
  getReconfirmCapMessage,
  RECONFIRM_SUBMIT_EXCLUDED_TOOLTIP,
} from '../deal-series/deal-series.constants';
import { QuantityCellValidationState } from '../deal-series/deal-series.types';
import { ICellRendererParams } from '@ag-grid-community/core';

/**
 * Order statuses that are eligible for reconfirmation
 * Orders must be in one of these statuses AND have a price change flag set
 */
export const statusCanReconfirm: Set<OrderStatusEnum> = new Set([
  OrderStatusEnum.SUBMITTED,
  OrderStatusEnum.ACCEPTED,
  OrderStatusEnum.SUBMITTED_AND_DRAFT,
]);

/**
 * Checks if an order has any price change flags set by the backend
 * These flags indicate that the order needs reconfirmation due to price changes
 */
export const hasAnyPriceChangeFlag = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  return !!(
    order.COUPON_CHANGED ||
    order.YIELD_CHANGED ||
    order.PRICE_CHANGED ||
    order.MATURITY_AMOUNT_CHANGED ||
    order.MATURITY_PRESENT_VALUE_CHANGED
  );
};

/**
 * Checks if an order has been reconfirmed.
 * Reconfirmed: IS_CONFIRMED = true and order is in a success status (ACCEPTED, SUBMITTED).
 */
export const isOrderReconfirmed = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  return (
    order.IS_CONFIRMED === true &&
    order.ORDER_STATUS != null &&
    successOrderStatuses.has(order.ORDER_STATUS)
  );
};

/** Instructed for reconfirm: success status (ACCEPTED, SUBMITTED) or SUBMITTED_AND_DRAFT. */
export const isOrderInstructedForReconfirm = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  return (
    (order.ORDER_STATUS != null && successOrderStatuses.has(order.ORDER_STATUS)) ||
    order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT
  );
};

/**
 * Re-confirmation eligibility from backend RECONFIRM_ELIGIBLE, optionally gated on deal reprice window.
 * Base conditions (min/max, multiples, quantity vs maturity) are NOT checked here — they only block Submit.
 *
 * @param dealStatus - When provided, false unless deal is in Verbal Award/CXL Window.
 */
export const isOrderEligibleForReconfirm = (
  order: MaturityOrder | null | undefined,
  dealStatus?: string | null,
): boolean => {
  if (!order) return false;
  if (order.RECONFIRM_ELIGIBLE !== true) return false;
  if (dealStatus != null && !isRepriceWindow(dealStatus)) return false;
  return true;
};

/** Split modal/grid lines inherit reconfirm eligibility from the parent maturity when the child row omits the flag. */
export const resolveSplitRowReconfirmEligible = (
  rowOrder: MaturityOrder,
  contextOrder: MaturityOrder | null | undefined,
  dealStatus?: string | null,
): boolean =>
  isOrderEligibleForReconfirm(rowOrder, dealStatus) ||
  isOrderEligibleForReconfirm(contextOrder, dealStatus);

/** True when a reconfirm-eligible instructed order should be reset to its accepted snapshot. */
export const isReconfirmResetTarget = (
  order: MaturityOrder,
  dealStatus?: string | null,
  contextOrder?: MaturityOrder | null,
): boolean => {
  if (!resolveSplitRowReconfirmEligible(order, contextOrder ?? null, dealStatus)) {
    return false;
  }
  if (isOrderInTransit(order)) {
    return false;
  }
  if (order.ORDER_STATUS === OrderStatusEnum.DRAFT) {
    return false;
  }
  if (order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT) {
    return true;
  }
  return orderHasEditsFromAcceptedBaseline(order);
};

/**
 * Per-row reset enablement during reconfirm. Cleanup must not use the global
 * dealHasEligibleOrdersToReset gate — split child lines can be eligible even
 * when the grid parent rollup omits RECONFIRM_ELIGIBLE.
 */
export const isReconfirmRowResetEnabled = (
  order: MaturityOrder,
  dealStatus: string | null | undefined,
  contextOrder?: MaturityOrder | null,
): boolean => {
  if (!isRepriceWindow(dealStatus)) {
    return true;
  }
  return resolveSplitRowReconfirmEligible(order, contextOrder ?? null, dealStatus);
};

/**
 * Global rule: at least one order line is eligible for re-confirmation.
 * Used to gate Edit (not per-row or clear-all reset — those resolve individual split children).
 * Pass submit-order lines when available; grid split-parent rollups omit RECONFIRM_ELIGIBLE.
 */
export const hasAtLeastOneReconfirmEligibleOrder = (
  orders: MaturityOrder[],
  dealStatus?: string | null,
): boolean => orders.some((o) => isOrderEligibleForReconfirm(o, dealStatus));

/**
 * True when this deal can be excluded from periodic refresh (reconfirm is once per deal / no orders to reconfirm).
 * (A) Not in Verbal and at least one order already confirmed → deal has left reconfirm window forever.
 * (B) In Verbal and no reconfirm-eligible orders → all submitted orders already reconfirmed.
 * (C) No orders that are instructed for reconfirm (no submitted/success orders) → nothing to reconfirm.
 * Only call when orders.length > 0 so we don't exclude before order data has loaded.
 */
export const shouldExcludeDealFromRefresh = (
  orders: MaturityOrder[],
  dealStatus?: string | null,
): boolean => {
  if (orders.length === 0) return false;
  const inVerbal = isRepriceWindow(dealStatus);
  const anyInstructed = orders.some((o) => isOrderInstructedForReconfirm(o));
  const anyReconfirmed = orders.some((o) => isOrderReconfirmed(o));
  if (!inVerbal && anyReconfirmed) return true; // (A) left reconfirm window, won't come back
  if (!anyInstructed) return true; // (C) no submitted orders, nothing to reconfirm
  return false;
};

/** User edited a price-changed order → SUBMITTED_AND_DRAFT. */
export const isOrderWithPriceChangesEdited = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  return hasAnyPriceChangeFlag(order) && order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT;
};

/**
 * Returns true when order is editable (not in transit, not reconfirmed).
 */
export const isOrderModifiable = (order: MaturityOrder | null | undefined): boolean =>
  !isOrderInTransit(order) && !isOrderReconfirmed(order);

/**
 * PP/ROP: all non–in-transit orders are editable. Verbal Award/CXL Window: only price-changed instructed orders.
 * All other deal statuses: same as isOrderModifiable (in-transit and reconfirmed still blocked).
 * Submit/cancel remain gated separately by deal status.
 */
export const isOrderEditableForDealStatus = (
  order: MaturityOrder | null | undefined,
  dealStatus: string | null | undefined
): boolean => {
  if (!order) return false;
  if (isDealStatusAllNonTransitEditable(dealStatus)) {
    return !isOrderInTransit(order);
  }

  if (!isOrderModifiable(order)) return false;
  if (isRepriceWindow(dealStatus)) {
    return hasAnyPriceChangeFlag(order) && isOrderInstructedForReconfirm(order);
  }
  return true;
};

/**
 * True when order quantity is above the persisted submitted cap (reconfirm-eligible, not reconfirmed, SUBMITTED_QUANTITY set).
 */
/**
 * Whether bulk/per-row All may set quantity to new par during reconfirm.
 * Mirrors server lastAcceptedOrSubmittedHadAutoReset for par-increase (not payload toggle).
 */
export const canApplyReconfirmParIncrease = (
  order: MaturityOrder | null | undefined,
  dealStatus?: string | null,
): boolean => {
  if (!order) return false;
  if (!dealStatus || !isRepriceWindow(dealStatus)) return true;
  if (!orderAmountChangeFlagActive(order)) return true;

  const status = order.ORDER_STATUS;
  if (status === OrderStatusEnum.ACCEPTED || status === OrderStatusEnum.SUBMITTED) {
    return order.IS_AUTO_RESET_ORDER_AMOUNT === true;
  }
  if (status === OrderStatusEnum.SUBMITTED_AND_DRAFT) {
    return order.IS_AUTO_RESET_ORDER_AMOUNT === true;
  }
  return order.IS_AUTO_RESET_ORDER_AMOUNT === true;
};

/**
 * Orders eligible for Global All apply/clear (bulk fill-to-par or reset).
 *
 * Reconfirm (Verbal Award/CXL Window):
 *   - RECONFIRM_ELIGIBLE (backend flag / blue status icons)
 *   - On submitted track (SUBMITTED/ACCEPTED/SUBMITTED_AND_DRAFT), not plain DRAFT
 *   - Not in-transit, not partial submit (SUBMITTED_QUANTITY < par — can't increase above submitted cap)
 *
 * Other deal statuses:
 *   - Deal-status editable (excludes in-transit, restricted statuses, etc.)
 */
export const getOrdersEligibleForGlobalAll = (
  orders: MaturityOrder[],
  dealStatus: string,
): MaturityOrder[] => {
  if (!isRepriceWindow(dealStatus)) {
    return orders.filter((o) => isOrderEditableForDealStatus(o, dealStatus));
  }
  return orders.filter(
    (o) =>
      !isOrderInTransit(o) &&
      isOrderEligibleForReconfirm(o, dealStatus) &&
      isOrderInstructedForReconfirm(o) &&
      !isSubmittedQuantityBelowPar(o),
  );
};

export const isQuantityAboveSubmittedCap = (order: MaturityOrder | null | undefined): boolean => {
  if (!order) return false;
  if (!hasAnyPriceChangeFlag(order)) return false;
  if (isOrderReconfirmed(order)) return false;
  if (orderAmountChangeFlagActive(order) && order.IS_AUTO_RESET_ORDER_AMOUNT === true) {
    return false;
  }
  const submittedQty = order.SUBMITTED_QUANTITY;
  if (submittedQty == null || submittedQty === '') return false;
  const sub = parseFloat(submittedQty);
  if (isNaN(sub)) return false;
  const qty = parseFloat(order.QUANTITY);
  if (isNaN(qty)) return false;
  return qty > sub;
};

/**
 * Checks if the deal is in a status that allows reconfirmation
 * Only deals in Verbal Award/CXL Window status can have orders reconfirmed
 */
export const isDealInReconfirmWindow = (dealStatus: string | null | undefined): boolean =>
  isRepriceWindow(dealStatus);

/**
 * Gets all orders that are eligible for reconfirmation from a list.
 * @param dealStatus - Optional. When provided, only returns orders eligible when deal is in Verbal Award/CXL Window.
 */
export const getOrdersEligibleForReconfirm = (
  orders: MaturityOrder[],
  dealStatus?: string | null,
): MaturityOrder[] => orders.filter((o) => isOrderEligibleForReconfirm(o, dealStatus));

/**
 * Checks if any order has been edited (is in SUBMITTED_AND_DRAFT status)
 * When an order is edited, we need to enable Review and Submit for all reconfirmable orders
 */
export const hasAnyEditedOrder = (orders: MaturityOrder[]): boolean => {
  return orders.some(order => order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT);
};

/** Reconfirm payload: IS_CONFIRMED=true; backend sets orderAction=Update. */
export const mapMaturityOrderForReconfirmation = (order: MaturityOrder) => {
  const baseOrder = mapMaturityOrderForSubmission(order);
  return { ...baseOrder, IS_CONFIRMED: true };
};

/** Payload for Review & Submit: reconfirm if eligible, else submission. */
export const mapOrderForSubmitOrReconfirm = (order: MaturityOrder, dealStatus?: string | null) =>
  isOrderEligibleForReconfirm(order, dealStatus)
    ? mapMaturityOrderForReconfirmation(order)
    : mapMaturityOrderForSubmission(order);

/**
 * Shared status icon configuration for deal-series grids (main grid + submit/cancel modals).
 * Keeps all blue / yellow / green reconfirm rules in one place.
 */
export const getStatusIconConfig = (
  params: ICellRendererParams<MaturityOrder>,
  dealStatusOverride?: string,
) => {
  const status = params.data.ORDER_STATUS;
  const dealStatus: string | undefined = dealStatusOverride ?? params.context?.dealStatus;
  const inReconfirmFlow = isRepriceWindow(dealStatus);

  // Outside reconfirm flow: original behaviour, no blue/yellow overrides.
  if (!inReconfirmFlow) {
    if (status != null && successOrderStatuses.has(status)) {
      if (params.data.ERROR_MESSAGE) {
        return {
          iconName: 'circle-check',
          iconColor: '#7acc79',
          iconSize: 'lg',
          secondaryIcon: 'circle-exclamation',
          secondaryIconColor: '#ffb660',
          secondaryIconSize: 'sm',
        };
      }
      return {
        iconName: 'circle-check',
        iconColor: '#7acc79',
        iconSize: 'lg',
      };
    }

    switch (status) {
      case OrderStatusEnum.PENDING:
      case OrderStatusEnum.SUBMITTED:
      case OrderStatusEnum.PENDING_CANCELLED:
      case OrderStatusEnum.SUBMITTED_AND_DRAFT:
      case OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE:
      case OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE:
        return {
          iconName: OrderStatusIcon[status],
          iconColor: MaturityStatusColor[status],
          iconSize: 'lg',
        };
    }
    return null;
  }

  // Reconfirm flow (Verbal Award/CXL Window): apply blue/yellow/green rules, but keep in-transit icons.
  const isReconfirmedOrder = isOrderReconfirmed(params.data);
  const needsReconfirm =
    status !== OrderStatusEnum.SUBMITTED_AND_DRAFT && isOrderEligibleForReconfirm(params.data);

  // In-transit (PENDING / PENDING_CANCELLED) should still use the dotted-circle style even in reconfirm flow.
  if (status === OrderStatusEnum.PENDING || status === OrderStatusEnum.PENDING_CANCELLED) {
    return {
      iconName: OrderStatusIcon[status],
      iconColor: MaturityStatusColor[status],
      iconSize: 'lg',
    };
  }

  // Failure statuses should still show their specific icons in reconfirm flow.
  if (status === OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE || status === OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE) {
    return {
      iconName: OrderStatusIcon[status],
      iconColor: MaturityStatusColor[status],
      iconSize: 'lg',
    };
  }

  // Yellow = previously submitted order that has been amended
  if (status === OrderStatusEnum.SUBMITTED_AND_DRAFT) {
    return {
      iconName: OrderStatusIcon[status],
      iconColor: '#ffb660',
      iconSize: 'lg',
    };
  }

  // Green = submitted order that has been reconfirmed
  if (isReconfirmedOrder) {
    if (params.data.ERROR_MESSAGE) {
      return {
        iconName: 'circle-check',
        iconColor: '#7acc79',
        iconSize: 'lg',
        secondaryIcon: 'circle-exclamation',
        secondaryIconColor: '#ffb660',
        secondaryIconSize: 'sm',
      };
    }
    return {
      iconName: 'circle-check',
      iconColor: '#7acc79',
      iconSize: 'lg',
    };
  }

  // Blue = previously submitted order that requires re-confirmation
  if (needsReconfirm) {
    return {
      iconName: 'circle-check',
      iconColor: '#4da3ff',
      iconSize: 'lg',
    };
  }

  // Other success-like statuses in reconfirm flow: fall back to standard green success icon.
  if (status != null && successOrderStatuses.has(status)) {
    if (params.data.ERROR_MESSAGE) {
      return {
        iconName: 'circle-check',
        iconColor: '#7acc79',
        iconSize: 'lg',
        secondaryIcon: 'circle-exclamation',
        secondaryIconColor: '#ffb660',
        secondaryIconSize: 'sm',
      };
    }
    return {
      iconName: OrderStatusIcon[status],
      iconColor: MaturityStatusColor[status],
      iconSize: 'lg',
    };
  }

  return null;
};

/**
 * Single-pass quantity validation for grid display. Use this in cellStyle, cellClassRules and tooltipValueGetter
 * to avoid recomputing hasValidOrderQuantity, validateOrderAmountForMultiples and breachesOrderConditions per cell.
 * During reconfirm window, orders excluded from Review and Submit are highlighted as errors (red).
 */
export const getQuantityCellValidationState = (
  order: MaturityOrder,
  dealStatus?: string | null,
): QuantityCellValidationState => {
  const base = getBaseQuantityCellValidationState(order);
  return applyReconfirmSubmitExclusionHighlight(order, dealStatus, base);
};

/**
 * Client-side eligibility for reconfirm submit highlighting (grid/modal).
 * Uses price-change flags and instructed status; complements server RECONFIRM_ELIGIBLE used for edit/submit actions.
 */
const isOrderEligibleForReconfirmSubmitHighlight = (
  order: MaturityOrder,
  dealStatus: string,
): boolean => {
  if (!hasAnyPriceChangeFlag(order)) return false;
  if (!isRepriceWindow(dealStatus)) return false;
  if (isQuantityAboveSubmittedCap(order)) return false;
  const isNotCancelled =
    order.ORDER_STATUS !== OrderStatusEnum.CANCELLED &&
    order.ORDER_STATUS !== OrderStatusEnum.PENDING_CANCELLED;
  return (
    isOrderInstructedForReconfirm(order) &&
    isNotCancelled &&
    !isOrderReconfirmed(order)
  );
};

/**
 * Price-changed instructed order that cannot be included in Review and Submit during reconfirm window.
 */
export const isOrderExcludedFromReconfirmSubmit = (
  order: MaturityOrder | null | undefined,
  dealStatus?: string | null,
): boolean => {
  if (!order || !dealStatus || !isRepriceWindow(dealStatus)) return false;
  if (!hasAnyPriceChangeFlag(order)) return false;
  if (!isOrderInstructedForReconfirm(order)) return false;
  if (isOrderReconfirmed(order)) return false;
  if (!hasOrderAmountApplied(order)) return false;
  return !(
    isOrderEligibleForReconfirmSubmitHighlight(order, dealStatus) &&
    isOrderPassesSubmissionAmountRules(order)
  );
};

/** Applies reconfirm-window submit exclusion styling on top of base quantity validation. */
export const applyReconfirmSubmitExclusionHighlight = (
  order: MaturityOrder,
  dealStatus: string | null | undefined,
  base: QuantityCellValidationState,
): QuantityCellValidationState => {
  if (!isOrderExcludedFromReconfirmSubmit(order, dealStatus)) {
    return base;
  }
  if (base.hasWarning && !base.hasError && isSplitMaturityParentRow(order)) {
    return base;
  }
  if (isQuantityAboveSubmittedCap(order)) {
    return {
      hasError: true,
      hasWarning: false,
      tooltip: getReconfirmCapMessage(order.SUBMITTED_QUANTITY),
    };
  }
  if (base.hasError) {
    return base;
  }
  const exclusionTooltip =
    base.tooltip ??
    (!isOrderPassesSubmissionAmountRules(order) ? RECONFIRM_SUBMIT_EXCLUDED_TOOLTIP : null);
  return {
    hasError: true,
    hasWarning: false,
    tooltip: exclusionTooltip ?? RECONFIRM_SUBMIT_EXCLUDED_TOOLTIP,
  };
};
